import '../styles/premium-slider.css';

const rootSelector = '[data-premium-slider-root]';

const selectors = {
    item: selector('item'),
    select: selector('select'),
    content: selector('content'),
    thumbLabel: selector('thumb-label'),
    pan: selector('pan-capture'),
    indicator: selector('indicator'),
    ring: selector('ring'),
};

function selector(name) {
    return `[data-premium-slider-${name}]:not(& ${rootSelector} [data-premium-slider-${name}])`;
}

function transitionMs(el) {
    const raw = getComputedStyle(el).transitionDuration.split(',')[0].trim();
    const value = parseFloat(raw) || 0;
    return raw.endsWith('ms') ? value : value * 1000;
}

// Auto-advances through the "next up" thumb (slot 0) on a timer, driving a
// countdown ring via a plain CSS transition on stroke-dashoffset rather than
// a rAF loop -- one transition per cycle, timed to match the interval
// exactly. The indicator itself is a play/pause toggle: pausing freezes the
// ring in place (never removes it) and resuming continues the drain for
// whatever time was left, rather than restarting a fresh interval. Returns
// a `pause` function; the caller wires it to any user-initiated advance,
// since that's the signal the user has taken over.
function initAutoAdvance(root, items, promote) {
    const intervalMs = Number(root.dataset.premiumSliderInterval) || 8000;
    const indicator = root.querySelector(selectors.indicator);
    const ring = root.querySelector(selectors.ring);

    let circumference = 0;
    if (ring && typeof ring.getTotalLength === 'function') {
        circumference = ring.getTotalLength();
        ring.style.strokeDasharray = String(circumference);
        ring.style.strokeDashoffset = '0';
    }

    let timeoutId = null;
    let playing = true;
    let cycleStart = 0;
    let cycleDuration = intervalMs;
    let remainingMs = intervalMs;

    function currentRingOffset() {
        if (!ring) return 0;
        const value = parseFloat(getComputedStyle(ring).strokeDashoffset);
        return Number.isFinite(value) ? value : 0;
    }

    function setIndicatorState(state) {
        if (!indicator) return;
        indicator.dataset.state = state;
        indicator.setAttribute('aria-label', state === 'playing' ? 'Pause slideshow' : 'Play slideshow');
    }

    function animateRing(fromOffset, duration) {
        if (!ring) return;
        // Snap to the starting offset with no transition, then -- on the
        // next frame, so the snap actually paints first -- kick off a fresh
        // linear depletion over exactly `duration`.
        ring.style.transitionDuration = '0s';
        ring.style.strokeDashoffset = String(fromOffset);
        void ring.getBoundingClientRect();
        window.requestAnimationFrame(() => {
            if (!playing) return;
            ring.style.transitionDuration = `${duration}ms`;
            ring.style.strokeDashoffset = String(circumference);
        });
    }

    function startCycle(duration, ringFromOffset = 0) {
        cycleStart = Date.now();
        cycleDuration = duration;
        animateRing(ringFromOffset, duration);
        timeoutId = setTimeout(() => {
            const next = items.find(item => item.dataset.state === 'thumb' && Number(item.style.getPropertyValue('--slot')) === 0);
            // Auto-advance is passive -- it must never steal focus from
            // whatever the user is doing elsewhere on the page (e.g. typing
            // in an editor), and moving focus would also scroll the newly
            // active slide into view out from under them.
            if (next) promote(items.indexOf(next), false);
            startCycle(intervalMs);
        }, duration);
    }

    function pause() {
        if (!playing) return;
        playing = false;
        clearTimeout(timeoutId);
        remainingMs = Math.max(0, cycleDuration - (Date.now() - cycleStart));
        if (ring) {
            const frozen = currentRingOffset();
            ring.style.transitionDuration = '0s';
            ring.style.strokeDashoffset = String(frozen);
        }
        setIndicatorState('paused');
    }

    function resume() {
        if (playing) return;
        playing = true;
        setIndicatorState('playing');
        startCycle(remainingMs, currentRingOffset());
    }

    function toggle() {
        if (playing) pause();
        else resume();
    }

    if (indicator) {
        indicator.addEventListener('click', toggle);
    }

    setIndicatorState('playing');
    startCycle(intervalMs);
    return pause;
}

function initPremiumSlider(root) {
    const items = Array.from(root.querySelectorAll(selectors.item));
    const total = items.length;
    if (total < 2) return;

    let activeIndex = items.findIndex(item => item.dataset.state === 'active');
    if (activeIndex < 0) activeIndex = 0;

    // The row is always the fixed original sequence, rotated to start right
    // after whichever index is active -- never an ad-hoc "most recently
    // shown" queue. Tapping ahead to slide 3 while slide 1 is active pushes
    // BOTH 1 and 2 to the end (in that order), landing on 4, 5, 1, 2, so the
    // row's relative order always stays legible as the user explores.
    function slotFor(index, active) {
        return (index - active - 1 + total) % total;
    }

    let locked = false;

    function updateA11y(focusActive) {
        items.forEach((item, index) => {
            const isActive = index === activeIndex;
            const select = item.querySelector(selectors.select);
            const content = item.querySelector(selectors.content);
            if (select) {
                select.inert = isActive;
                select.setAttribute('aria-hidden', String(isActive));
            }
            if (content) {
                content.inert = !isActive;
                content.setAttribute('aria-hidden', String(!isActive));
                if (isActive && focusActive) content.focus();
            }
        });
    }

    function promote(index, focusActive = true) {
        if (locked || index < 0 || index === activeIndex) return;
        locked = true;

        const incoming = items[index];
        const outgoing = items[activeIndex];
        const outgoingIndex = activeIndex;
        const newActive = index;
        const outgoingSlot = slotFor(outgoingIndex, newActive);

        // The rest of the row advances immediately -- this is the desired
        // "row advances while the grow transition plays" motion. Only the
        // outgoing slide itself is held back (see below).
        items.forEach((item, i) => {
            if (i === index || i === outgoingIndex) return;
            item.dataset.state = 'thumb';
            item.style.setProperty('--slot', slotFor(i, newActive));
        });

        // The real outgoing slide can't join the row yet -- it's still doing
        // active-slide duty underneath the incoming grow -- so its future
        // slot would otherwise sit empty for the whole transition and then
        // have it "pop in" abruptly at the end. A disposable clone stands in
        // for it instead, entering the row the same way a real member would:
        // it starts one slot further along than its real destination (just
        // past the end of the visible row) and is immediately walked back to
        // its true slot, so it rides the same left-shift transition as the
        // rest of the row instead of snapping straight into place. It's
        // discarded the instant the real node lands in the same spot
        // (finishPromote), and since both are pixel-identical by then the
        // swap itself is imperceptible. It needs no click handling of its
        // own (nothing is interactive during a transition anyway) and is
        // marked inert/aria-hidden so it can't be focused or announced as a
        // second copy of the slide.
        const outgoingClone = outgoing.cloneNode(true);
        outgoingClone.dataset.premiumSliderClone = 'true';
        outgoingClone.dataset.state = 'thumb';
        outgoingClone.setAttribute('aria-hidden', 'true');
        outgoingClone.inert = true;
        outgoingClone.style.setProperty('--slot', outgoingSlot + 1);
        outgoing.insertAdjacentElement('afterend', outgoingClone);
        void outgoingClone.offsetWidth; // commit the off-row starting slot before...
        outgoingClone.style.setProperty('--slot', outgoingSlot); // ...animating into its real one, in step with the rest of the row

        // The incoming thumb grows into the active slot. `data-entering`
        // bumps it above the still-untouched outgoing slide (which is also
        // `data-state="active"` right now) so it visibly grows over/occludes
        // the old slide, rather than the old slide appearing to slide away
        // out from under it.
        incoming.dataset.entering = 'true';
        activeIndex = newActive;
        incoming.dataset.state = 'active';
        updateA11y(focusActive);

        let settled = false;
        const finishPromote = () => {
            if (settled) return;
            settled = true;
            incoming.removeEventListener('transitionend', onGrowEnd);
            clearTimeout(fallback);
            delete incoming.dataset.entering;

            // Only now -- fully hidden underneath the now fully-grown incoming
            // slide -- snap the outgoing slide (box + its visual layers, so
            // the crossfade doesn't lag behind the box) straight into its new
            // end-of-row thumbnail slot with no visible motion, then discard
            // the clone that was standing in for it -- both are showing the
            // exact same pixels in the exact same slot, so the swap itself
            // is invisible.
            const outgoingSelect = outgoing.querySelector(selectors.select);
            const outgoingContent = outgoing.querySelector(selectors.content);
            const outgoingThumbLabel = outgoing.querySelector(selectors.thumbLabel);
            const outgoingLayers = [outgoing, outgoingSelect, outgoingContent, outgoingThumbLabel].filter(Boolean);
            outgoingLayers.forEach(el => { el.style.transitionDuration = '0s'; });
            outgoing.dataset.state = 'thumb';
            outgoing.style.setProperty('--slot', outgoingSlot);
            void outgoing.offsetHeight;
            outgoingLayers.forEach(el => { el.style.transitionDuration = ''; });
            outgoingClone.remove();

            locked = false;
        };

        function onGrowEnd(event) {
            if (event.target !== incoming || event.propertyName !== 'width') return;
            finishPromote();
        }
        incoming.addEventListener('transitionend', onGrowEnd);
        // Safety net: if transitionend never fires for some reason, don't
        // leave the slider permanently locked.
        const fallback = setTimeout(finishPromote, transitionMs(incoming) + 100);
    }

    const pauseAutoAdvance = initAutoAdvance(root, items, promote);

    items.forEach((item, index) => {
        const select = item.querySelector(selectors.select);
        if (select) {
            select.addEventListener('click', () => {
                pauseAutoAdvance();
                promote(index);
            });
        }
    });

    const pan = root.querySelector(selectors.pan);
    if (pan) {
        let raf = null;
        pan.addEventListener('scroll', () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                root.style.setProperty('--row-pan', `${pan.scrollLeft}px`);
                raf = null;
            });
        });
        pan.addEventListener('click', event => {
            pan.style.pointerEvents = 'none';
            const hit = document.elementFromPoint(event.clientX, event.clientY);
            pan.style.pointerEvents = '';
            const item = hit && hit.closest(selectors.item);
            if (item) {
                pauseAutoAdvance();
                promote(items.indexOf(item));
            }
        });
    }

    updateA11y(false);
}

function init() {
    document.querySelectorAll(rootSelector).forEach(root => {
        initPremiumSlider(root);
    });
}

export default {
    init,
    initPremiumSlider
};
